import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Eye, EyeOff, Mail, Lock, RefreshCw } from "lucide-react";
import Turnstile from "@/components/ui/Turnstile";

interface AuthModalProps {
  showLogin: boolean;
  setShowLogin: (show: boolean) => void;
  setShowOnboarding?: (show: boolean) => void;
  setUserEmail?: (email: string) => void;
}

type Mode = "signin" | "signup" | "reset";

const AuthModal: React.FC<AuthModalProps> = ({
  showLogin,
  setShowLogin,
  setShowOnboarding,
  setUserEmail,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showSignupCta, setShowSignupCta] = useState(false);
  const [showResendConfirmation, setShowResendConfirmation] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [lastLoginMethod, setLastLoginMethod] = useState<
    "google" | "linkedin" | "email" | null
  >(
    () =>
      localStorage.getItem("lastLoginMethod") as
        | "google"
        | "linkedin"
        | "email"
        | null,
  );
  const [googleHint, setGoogleHint] = useState<{
    name: string;
    email: string;
    picture: string;
    credential: string;
  } | null>(null);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && showLogin) {
        setShowLogin(false);
        resetForms();
      }
    };

    if (showLogin) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [showLogin, setShowLogin]);

  // Google Identity Services — detect signed-in Google account
  useEffect(() => {
    if (!showLogin) return;
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) return;

    const initGIS = () => {
      const g = (window as any).google;
      if (!g?.accounts?.id) return;
      g.accounts.id.initialize({
        client_id: clientId,
        callback: (response: { credential: string }) => {
          try {
            const payload = JSON.parse(atob(response.credential.split(".")[1]));
            setGoogleHint({
              name: payload.name,
              email: payload.email,
              picture: payload.picture,
              credential: response.credential,
            });
          } catch {
            // ignore decode errors
          }
        },
        auto_select: true,
        cancel_on_tap_outside: false,
        itp_support: true,
      });
      // Trigger detection; suppress One Tap overlay — we only want the hint
      g.accounts.id.prompt((notification: any) => {
        if (notification.isDisplayMoment() && !notification.isAutoSelected()) {
          g.accounts.id.cancel();
        }
      });
    };

    if ((window as any).google?.accounts?.id) {
      initGIS();
    } else {
      const existingScript = document.getElementById("gis-script");
      if (existingScript) {
        existingScript.addEventListener("load", initGIS);
      } else {
        const script = document.createElement("script");
        script.id = "gis-script";
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.onload = initGIS;
        document.head.appendChild(script);
      }
    }

    return () => {
      (window as any).google?.accounts?.id?.cancel?.();
    };
  }, [showLogin]);

  const resetForms = () => {
    setMode("signin");
    setEmail("");
    setPassword("");
    setShowPassword(false);
    setError("");
    setShowSignupCta(false);
    setShowResendConfirmation(false);
    setResendSuccess(false);
    setSuccessMessage("");
    setCaptchaToken(null);
  };

  // Detect link_expired error from AuthCallback redirect and pre-open resend UI
  useEffect(() => {
    if (!showLogin) return;
    const params = new URLSearchParams(location.search);
    if (params.get("error") === "link_expired") {
      setMode("signin");
      setError("");
      setShowResendConfirmation(true);
      // Clean the URL so a refresh doesn't re-trigger this
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [showLogin, location.search]);

  // ── Google OAuth ──────────────────────────────────────────────────────────
  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      setError("");

      const currentPath = window.location.pathname;
      if (currentPath !== "/") {
        sessionStorage.setItem("redirectAfterAuth", currentPath);
      }

      // If GIS detected an account credential, sign in directly (no redirect)
      if (googleHint?.credential) {
        const { data, error } = await supabase.auth.signInWithIdToken({
          provider: "google",
          token: googleHint.credential,
        });
        if (!error && data.user) {
          localStorage.setItem("lastLoginMethod", "google");
          setLastLoginMethod("google");
          if (data.user.email) {
            localStorage.setItem("googleLoginEmail", data.user.email);
          }
          setShowLogin(false);
          // Clean up any error query params left in the URL (e.g. ?error=auth_failed)
          if (new URLSearchParams(location.search).has("error")) {
            navigate("/", { replace: true });
          }
          if (
            !data.user.user_metadata?.onboarding_completed &&
            setShowOnboarding &&
            setUserEmail
          ) {
            setUserEmail(data.user.email ?? "");
            setShowOnboarding(true);
          }
          return;
        }
        // Credential expired — clear hint and fall through to OAuth redirect
        setGoogleHint(null);
      }

      const knownEmail =
        googleHint?.email ??
        localStorage.getItem("googleLoginEmail") ??
        undefined;
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            ...(knownEmail ? { login_hint: knownEmail } : {}),
          },
        },
      });
      if (error) {
        setError(error.message || "Google sign-in failed. Please try again.");
      } else {
        localStorage.setItem("lastLoginMethod", "google");
        setLastLoginMethod("google");
      }
    } catch (err: any) {
      setError(err?.message || "Google sign-in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── LinkedIn OAuth ────────────────────────────────────────────────────────
  const handleLinkedInSignIn = async () => {
    try {
      setLoading(true);
      setError("");

      const currentPath = window.location.pathname;
      if (currentPath !== "/") {
        sessionStorage.setItem("redirectAfterAuth", currentPath);
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: "linkedin_oidc",
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) {
        setError(error.message || "LinkedIn sign-in failed. Please try again.");
      } else {
        localStorage.setItem("lastLoginMethod", "linkedin");
        setLastLoginMethod("linkedin");
      }
    } catch (err: any) {
      setError(err?.message || "LinkedIn sign-in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Email sign-up ─────────────────────────────────────────────────────────
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          ...(captchaToken ? { captchaToken } : {}),
        },
      });
      if (error) {
        const status = (error as any).status as number | undefined;
        console.error("[Signup] Supabase error:", {
          message: error.message,
          status,
          code: (error as any).code,
          name: error.name,
          full: error,
        });
        let msg: string;
        if (status === 504) {
          msg =
            "The server timed out — this is usually caused by Supabase trying to send a confirmation email. " +
            "Check Authentication → Settings in your Supabase dashboard and verify your SMTP config (or temporarily disable email confirmations).";
        } else if (status === 429) {
          msg = "Too many requests. Please wait a moment and try again.";
        } else if (status && status >= 500) {
          msg = `Server error (${status}). This is on Supabase's side — check status.supabase.com or try again shortly.`;
        } else {
          msg =
            error.message ||
            `Error ${status ?? ""} ${(error as any).code ?? ""}`.trim() ||
            "Sign-up failed. Please try again.";
        }
        setError(msg);
      } else {
        console.log("[Signup] Success:", {
          userId: data.user?.id,
          email: data.user?.email,
          identities: data.user?.identities?.length,
          session: !!data.session,
        });
        setSuccessMessage(
          "Account created! Check your email and click the confirmation link to activate it.",
        );
        setEmail("");
        setPassword("");
        setCaptchaToken(null);
      }
    } catch (err: any) {
      console.error("[Signup] Unexpected exception:", err);
      setError(err?.message || "Sign-up failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Email sign-in ─────────────────────────────────────────────────────────
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setShowResendConfirmation(false);
    setResendSuccess(false);
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: captchaToken ? { captchaToken } : undefined,
      });
      if (error) {
        const status = (error as any).status as number | undefined;
        console.error("[SignIn] Supabase error:", {
          message: error.message,
          status,
          code: (error as any).code,
          name: error.name,
          full: error,
        });
        let msg: string;
        let credentialError = false;
        const isEmailNotConfirmed =
          error.message?.toLowerCase().includes("email not confirmed") ||
          (error as any).code === "email_not_confirmed";
        if (status === 504) {
          msg = "The server timed out. Please try again in a moment.";
        } else if (status === 429) {
          msg = "Too many requests. Please wait a moment and try again.";
        } else if (status && status >= 500) {
          msg = `Server error (${status}). This is on Supabase's side — check status.supabase.com or try again shortly.`;
        } else if (isEmailNotConfirmed) {
          // Don't set generic error text — the resend panel handles this case
          msg = "";
          setShowResendConfirmation(true);
        } else if (
          error.message?.toLowerCase().includes("invalid login credentials") ||
          error.message?.toLowerCase().includes("invalid email or password") ||
          (error as any).code === "invalid_credentials"
        ) {
          msg = "Incorrect email or password.";
          credentialError = true;
        } else {
          msg =
            error.message ||
            `Error ${status ?? ""} ${(error as any).code ?? ""}`.trim() ||
            "Sign-in failed. Please try again.";
        }
        setError(msg);
        setShowSignupCta(credentialError);
      } else {
        localStorage.setItem("lastLoginMethod", "email");
        setLastLoginMethod("email");
        setShowLogin(false);
        resetForms();
        if (
          !data.user?.user_metadata?.onboarding_completed &&
          setShowOnboarding &&
          setUserEmail
        ) {
          setUserEmail(data.user?.email ?? "");
          setShowOnboarding(true);
        }
      }
    } catch (err: any) {
      setError(err?.message || "Sign-in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Resend confirmation email ──────────────────────────────────────────────
  const handleResendConfirmation = async () => {
    if (!email) {
      setError("Please enter your email address above, then click resend.");
      return;
    }
    setResendLoading(true);
    setResendSuccess(false);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) {
        setError(error.message || "Failed to resend. Please try again.");
      } else {
        setResendSuccess(true);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to resend. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  // ── Password reset ────────────────────────────────────────────────────────
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) {
        setError(error.message);
      } else {
        setSuccessMessage(
          "If an account exists for that email, you’ll receive a password reset link shortly. Please check your inbox (and spam/junk).",
        );
      }
    } catch (err: any) {
      setError(err?.message || "Failed to send reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {showLogin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0 z-0">
            <img
              src="/earth.webp"
              alt="Earth from space"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-sky-200/10" />
            <div className="absolute bottom-6 left-8 z-10">
              <a
                href="https://www.vecteezy.com/free-photos/earth-from-space"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] text-white/30 hover:text-white/60 transition-colors uppercase tracking-widest font-medium"
              >
                Earth From Space Stock photo by Vecteezy
              </a>
            </div>
          </div>

          {/* Branding */}
          <div
            className="absolute top-8 left-8 z-10 flex items-center gap-3 cursor-pointer group"
            onClick={() => {
              setShowLogin(false);
              navigate("/");
            }}
          >
            <span className="text-2xl font-bold text-white/70 hover:text-white group-hover:drop-shadow-sm">
              Myǃhū
            </span>
          </div>

          {/* Close */}
          <button
            className="absolute top-8 right-8 p-3 rounded-full bg-white/50 hover:bg-white/80 text-gray-600 hover:text-gray-900 transition-all z-20 backdrop-blur-md border border-white/50 shadow-sm"
            onClick={() => {
              setShowLogin(false);
              resetForms();
            }}
            aria-label="Close modal"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {/* Card */}
          <div className="relative z-10 w-full max-w-[460px] max-h-[90vh] overflow-y-auto no-scrollbar bg-white/60 backdrop-blur-2xl rounded-[2.5rem] p-8 md:p-12 shadow-[0_20px_50px_rgba(0,0,0,0.1),0_0_30px_rgba(64,224,208,0.3)] border-4 border-blue-200/60 flex flex-col items-center animate-in fade-in zoom-in duration-300">
            {/* Heading */}
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {mode === "reset"
                  ? "Reset Password"
                  : mode === "signup"
                    ? "Create Account"
                    : "Sign in"}
              </h2>
              <p className="text-gray-600 text-[0.95rem] leading-relaxed">
                {mode === "reset"
                  ? "Enter your email to receive a password reset link"
                  : mode === "signup"
                    ? "Start measuring environmental impact with data-driven insight"
                    : "Welcome back! Continue your work monitoring environmental impact"}
              </p>
            </div>

            <div className="w-full">
              {/* ── Forgot Password ── */}
              {mode === "reset" ? (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400 group-focus-within:text-[#40e0d0] transition-colors" />
                    </div>
                    <input
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 bg-white/50 border border-white/20 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#40e0d0]/50 focus:bg-white transition-all text-gray-900 placeholder:text-gray-400 shadow-sm"
                      required
                      autoComplete="email"
                      autoFocus
                    />
                  </div>

                  {error && (
                    <div className="p-3 bg-red-50/50 border border-red-200 rounded-xl text-sm text-red-600">
                      {error}
                    </div>
                  )}
                  {successMessage && (
                    <div className="p-3 bg-green-50/50 border border-green-200 rounded-xl text-sm text-green-600">
                      {successMessage}
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full bg-[#1e293b] hover:bg-black text-white font-semibold py-7 rounded-2xl transition-all shadow-lg active:scale-[0.98] disabled:opacity-50"
                    disabled={loading}
                  >
                    {loading ? "Sending…" : "Send reset link"}
                  </Button>

                  <div className="text-center pt-2">
                    <button
                      type="button"
                      className="text-gray-500 hover:text-gray-800 font-medium text-sm transition-colors"
                      onClick={() => {
                        setMode("signin");
                        setError("");
                        setShowSignupCta(false);
                        setSuccessMessage("");
                      }}
                    >
                      ← Back to sign in
                    </button>
                  </div>
                </form>
              ) : (
                /* ── Sign in / Sign up ── */
                <>
                  {/* OAuth buttons */}
                  <div className="flex flex-col gap-3 w-full mb-6">
                    {/* Google */}
                    <div className="relative w-full">
                      {lastLoginMethod === "google" && !googleHint && (
                        <span className="absolute -top-2.5 right-2 bg-teal-500 text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full z-10 pointer-events-none">
                          Last used
                        </span>
                      )}
                      <button
                        onClick={handleGoogleSignIn}
                        disabled={loading}
                        aria-label={
                          googleHint
                            ? `Continue as ${googleHint.name}`
                            : "Continue with Google"
                        }
                        className="w-full flex items-center justify-center gap-2 py-3.5 bg-white/80 hover:bg-white border border-white/60 rounded-xl shadow-sm hover:shadow-md transition-all active:scale-95 overflow-hidden"
                      >
                        {googleHint ? (
                          <>
                            <img
                              src={googleHint.picture}
                              alt={googleHint.name}
                              className="w-8 h-8 rounded-full flex-shrink-0 ring-2 ring-blue-300"
                              referrerPolicy="no-referrer"
                            />
                            <div className="flex flex-col items-start leading-tight min-w-0">
                              <span className="text-s font-semibold text-gray-800 truncate max-w-[120px]">
                                {googleHint.name.split(" ")[0]}
                              </span>
                              <span className="text-[.8rem] text-gray-400 truncate max-w-[180px]">
                                {googleHint.email}
                              </span>
                            </div>
                          </>
                        ) : (
                          <>
                            <svg
                              className="w-5 h-5 flex-shrink-0"
                              viewBox="0 0 24 24"
                            >
                              <path
                                fill="#4285F4"
                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                              />
                              <path
                                fill="#34A853"
                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                              />
                              <path
                                fill="#FBBC05"
                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                              />
                              <path
                                fill="#EA4335"
                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                              />
                            </svg>
                            <span className="text-sm font-medium text-gray-700">
                              Google
                            </span>
                          </>
                        )}
                      </button>
                    </div>

                    {/* LinkedIn */}
                    <div className="relative w-full">
                      {lastLoginMethod === "linkedin" && (
                        <span className="absolute -top-2.5 right-2 bg-teal-500 text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full z-10 pointer-events-none">
                          Last used
                        </span>
                      )}
                      <button
                        onClick={handleLinkedInSignIn}
                        disabled={loading}
                        aria-label="Continue with LinkedIn"
                        className="w-full flex items-center justify-center gap-2 py-3.5 bg-white/80 hover:bg-white border border-white/60 rounded-xl shadow-sm hover:shadow-md transition-all active:scale-95"
                      >
                        <svg
                          className="w-5 h-5 flex-shrink-0"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <rect width="24" height="24" rx="4" fill="#0A66C2" />
                          <path
                            d="M7.5 9.5H5V19h2.5V9.5ZM6.25 8.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3ZM19 19h-2.5v-4.75c0-1.15-.42-1.75-1.3-1.75-.96 0-1.45.65-1.45 1.75V19H11.5V9.5H14v1.1c.46-.76 1.27-1.35 2.3-1.35C18.1 9.25 19 10.5 19 12.9V19Z"
                            fill="white"
                          />
                        </svg>
                        <span className="text-sm font-medium text-gray-700">
                          LinkedIn
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="relative w-full my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200/70" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="bg-white px-3 text-gray-400 tracking-wider rounded-full">
                        or continue with email
                      </span>
                    </div>
                  </div>

                  {/* Email / Password form */}
                  <form
                    onSubmit={mode === "signup" ? handleSignup : handleSignIn}
                    className="space-y-4"
                  >
                    {/* Email */}
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400 group-focus-within:text-[#40e0d0] transition-colors" />
                      </div>
                      <input
                        type="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-12 pr-4 py-4 bg-white/50 border border-white/20 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#40e0d0]/50 focus:bg-white transition-all text-gray-900 placeholder:text-gray-400 shadow-sm"
                        required
                        autoComplete="email"
                      />
                    </div>

                    {/* Password */}
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-[#40e0d0] transition-colors" />
                      </div>
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full pl-12 pr-12 py-4 bg-white/50 border border-white/20 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#40e0d0]/50 focus:bg-white transition-all text-gray-900 placeholder:text-gray-400 shadow-sm"
                        required
                        minLength={6}
                        autoComplete={
                          mode === "signup"
                            ? "new-password"
                            : "current-password"
                        }
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showPassword ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </button>
                    </div>

                    {/* Forgot password link */}
                    {mode === "signin" && (
                      <div className="text-right">
                        <button
                          type="button"
                          className="text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors"
                          onClick={() => {
                            setMode("reset");
                            setError("");
                            setShowSignupCta(false);
                            setShowResendConfirmation(false);
                            setResendSuccess(false);
                            setSuccessMessage("");
                          }}
                        >
                          Forgot password?
                        </button>
                      </div>
                    )}

                    {/* Terms and Privacy Checkbox */}
                    <div className="flex items-center gap-2 pt-2">
                      <input
                        id="accept-terms"
                        type="checkbox"
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        className="accent-[#2bbdb3] w-4 h-4 rounded border-gray-300 focus:ring-[#40e0d0]"
                        required
                      />
                      <label
                        htmlFor="accept-terms"
                        className="text-sm text-gray-600 select-none"
                      >
                        I accept the{" "}
                        <a
                          href="/terms-and-privacy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline text-[#2bbdb3] hover:text-black"
                        >
                          Terms and Privacy Policy
                        </a>
                      </label>
                    </div>

                    {/* Cloudflare Turnstile */}
                    <Turnstile
                      siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                      onSuccess={(token) => setCaptchaToken(token)}
                      onExpire={() => setCaptchaToken(null)}
                      onError={() => setCaptchaToken(null)}
                      className="mt-1"
                    />

                    {/* Error / success */}
                    {error && (
                      <div className="p-3 bg-red-50/50 border border-red-200 rounded-xl text-sm text-red-600">
                        <p>{error}</p>
                        {showSignupCta && mode === "signin" && (
                          <div className="mt-2 pt-2 border-t border-red-100 flex items-center gap-1 flex-wrap">
                            <span className="text-gray-500">
                              No account yet?
                            </span>
                            <button
                              type="button"
                              className="font-semibold text-[#2bbdb3] hover:underline transition-colors"
                              onClick={() => {
                                setMode("signup");
                                setError("");
                                setShowSignupCta(false);
                                setPassword("");
                              }}
                            >
                              Create a free account →
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Email not confirmed / link expired — resend panel */}
                    {showResendConfirmation && mode === "signin" && (
                      <div className="p-4 bg-amber-50/70 border border-amber-200 rounded-xl text-sm space-y-3 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <Mail className="h-5 w-5 text-amber-500 flex-shrink-0" />
                          <div>
                            <p className="font-semibold text-amber-800 mb-2">
                              Email not confirmed yet
                            </p>
                            <p className="text-amber-700 mt-0.5">
                              You need to verify your email before signing in.
                              Confirmation links expire after{" "}
                              <span className="font-medium">1 hour</span>. Check
                              your inbox (and spam folder), or request a new
                              link below.
                            </p>
                          </div>
                        </div>
                        {resendSuccess ? (
                          <div className="flex items-center justify-center gap-2 text-green-700 font-medium">
                            <svg
                              className="w-4 h-4 flex-shrink-0"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            Confirmation email sent! Check your inbox.
                          </div>
                        ) : (
                          <button
                            type="button"
                            disabled={resendLoading}
                            onClick={handleResendConfirmation}
                            className="inline-flex items-center justify-center gap-1.5 font-semibold text-amber-800 hover:text-amber-900 hover:underline transition-colors disabled:opacity-50"
                          >
                            <RefreshCw
                              className={`h-3.5 w-3.5 ${resendLoading ? "animate-spin" : ""}`}
                            />
                            {resendLoading
                              ? "Sending…"
                              : "Resend confirmation email"}
                          </button>
                        )}
                      </div>
                    )}

                    {successMessage && (
                      <div className="p-3 bg-green-50/50 border border-green-200 rounded-xl text-sm text-green-600">
                        {successMessage}
                      </div>
                    )}

                    <Button
                      type="submit"
                      className="w-full bg-[#1e293b] hover:bg-black text-white font-semibold py-7 rounded-2xl transition-all shadow-lg active:scale-[0.98] disabled:opacity-50"
                      disabled={loading || !acceptedTerms || !captchaToken}
                    >
                      {loading
                        ? "Processing…"
                        : mode === "signup"
                          ? "Create account"
                          : "Sign in"}
                    </Button>
                  </form>

                  {/* Toggle sign in / sign up */}
                  <div className="mt-8 text-center pt-6 border-t border-gray-100/30 w-full">
                    <p className="text-[0.9rem] text-gray-500">
                      {mode === "signup"
                        ? "Already have an account?"
                        : "Don't have an account?"}{" "}
                      <button
                        type="button"
                        className="text-black hover:text-[#2bbdb3] font-semibold transition-colors hover:underline"
                        onClick={() => {
                          const nextMode =
                            mode === "signup" ? "signin" : "signup";
                          setMode(nextMode);
                          setError("");
                          setShowSignupCta(false);
                          setShowResendConfirmation(false);
                          setResendSuccess(false);
                          setSuccessMessage("");
                          // Keep email pre-filled when switching so the user doesn't retype it
                          setPassword("");
                        }}
                      >
                        {mode === "signup"
                          ? "Sign in instead"
                          : "Create one for free"}
                      </button>
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AuthModal;
